/**
 * Image scaling and encoding utility for profile pictures
 * Scales images to max 400x400px and encodes as JPEG base64 data URI
 */
export class ImageScaler {
  static readonly MAX_SIZE = 400 // 400x400px max dimension
  static readonly JPEG_QUALITY = 0.8 // 80% quality
  static readonly MAX_BASE64_SIZE = 100 * 1024 // 100KB base64 string limit

  /**
   * Scale an image file and encode as JPEG base64 data URI
   * Transcodes all formats (PNG, WebP, GIF) to JPEG
   */
  static async scaleAndEncode(file: File): Promise<string> {
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp|gif)$/i)) {
      throw new Error('Only JPEG, PNG, WebP, and GIF images are supported')
    }

    // Validate file size before processing (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image file must be smaller than 5MB')
    }

    // Load image
    const img = await this.loadImage(file)

    // Calculate dimensions (maintain aspect ratio)
    const { width, height } = this.calculateDimensions(img.width, img.height, this.MAX_SIZE)

    // Scale and convert to JPEG
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Failed to get canvas context')
    }

    canvas.width = width
    canvas.height = height

    // Draw white background (for transparency in PNGs)
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, width, height)

    // Draw scaled image
    ctx.drawImage(img, 0, 0, width, height)

    // Convert to JPEG data URL with compression
    const dataUrl = canvas.toDataURL('image/jpeg', this.JPEG_QUALITY)

    // Validate final size (base64 encoding adds ~33% overhead)
    if (dataUrl.length > this.MAX_BASE64_SIZE) {
      const sizeKB = Math.round((dataUrl.length * 0.75) / 1024)
      throw new Error(
        `Compressed image is ${sizeKB}KB, must be under 75KB. Try a simpler image with fewer details.`,
      )
    }

    return dataUrl
  }

  /**
   * Load image file into HTMLImageElement
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)

      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve(img)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load image'))
      }

      img.src = url
    })
  }

  /**
   * Calculate scaled dimensions maintaining aspect ratio
   */
  private static calculateDimensions(
    width: number,
    height: number,
    maxSize: number,
  ): { width: number; height: number } {
    if (width <= maxSize && height <= maxSize) {
      return { width, height }
    }

    const ratio = width / height
    if (width > height) {
      return { width: maxSize, height: Math.round(maxSize / ratio) }
    } else {
      return { width: Math.round(maxSize * ratio), height: maxSize }
    }
  }
}
